/**
 * Module de gestion du bandeau hors ligne
 */

class OfflineBanner {
  private banner: HTMLElement | null = null;
  private isOnline: boolean = navigator.onLine;

  constructor() {
    // Attendre que le DOM soit prêt
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.init();
      });
    } else {
      // DOM déjà chargé
      this.init();
    }
  }

  private async checkNetworkStatus(): Promise<boolean> {
    // Vérifier navigator.onLine d'abord (rapide)
    if (!navigator.onLine) {
      return false;
    }

    // Faire une vérification réseau réelle avec un timeout court
    // Utiliser une URL externe pour éviter le cache du service worker
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // Timeout de 2 secondes

      // Essayer d'abord avec l'origine du site
      try {
        const response = await fetch(
          window.location.origin +
            '/wp-admin/admin-ajax.php?action=heartbeat&_=' +
            Date.now(),
          {
            method: 'GET',
            cache: 'no-store', // Force le bypass du cache
            signal: controller.signal,
            credentials: 'omit' // Ne pas envoyer de cookies
          }
        );

        clearTimeout(timeoutId);
        return response.ok;
      } catch (e) {
        // Si ça échoue, essayer avec une URL externe simple
        const externalResponse = await fetch(
          'https://www.google.com/favicon.ico?nocache=' + Date.now(),
          {
            method: 'HEAD',
            cache: 'no-store',
            mode: 'no-cors', // Permet de contourner CORS
            signal: controller.signal
          }
        );

        clearTimeout(timeoutId);
        return true; // Si on arrive ici, on est connecté
      }
    } catch (error) {
      // Erreur réseau = hors ligne
      return false;
    }
  }

  private async init(): Promise<void> {
    // Trouver le bandeau dans le DOM (créé par Twig)
    this.banner = document.querySelector('.offline-banner') as HTMLElement;

    // Si le bandeau n'existe pas, ne rien faire
    if (!this.banner) {
      return;
    }

    // Vérification immédiate avec navigator.onLine (réactif)
    this.isOnline = navigator.onLine;
    if (!this.isOnline) {
      this.showBanner();
    } else {
      this.removeBanner();
    }

    // Vérification réseau réelle en arrière-plan (pour confirmer)
    this.checkNetworkStatus().then((isOnline) => {
      this.isOnline = isOnline;
      if (!this.isOnline) {
        this.showBanner();
      } else {
        this.removeBanner();
      }
    });

    // Écouter les événements de changement d'état de connexion
    window.addEventListener('online', () => {
      // Réaction immédiate
      this.isOnline = navigator.onLine;
      if (this.isOnline) {
        this.removeBanner();
      }
      // Vérification réseau réelle en arrière-plan
      setTimeout(async () => {
        this.isOnline = await this.checkNetworkStatus();
        if (this.isOnline) {
          this.removeBanner();
        } else {
          this.showBanner();
        }
      }, 500);
    });

    window.addEventListener('offline', () => {
      // Réaction immédiate
      this.isOnline = false;
      this.showBanner();
    });
  }

  private showBanner(): void {
    if (this.banner) {
      this.banner.classList.add('offline-banner--show');
    }
  }

  private removeBanner(): void {
    if (this.banner) {
      this.banner.classList.remove('offline-banner--show');
      // Supprimer complètement du DOM si en ligne
      if (this.isOnline && this.banner.parentNode) {
        this.banner.parentNode.removeChild(this.banner);
        this.banner = null;
      }
    }
  }
}

export const offlineBanner = new OfflineBanner();
